// Engagement API methods — mixin applied to the ApiService prototype chain.
// Methods run with `this` bound to the ApiService instance, so this.request()
// and cross-domain calls resolve through the shared HttpClient base.
const withEngagementApi = (Base) => class extends Base {
  // Extension request endpoints
  async listMyExtensions(params = {}) {
    const q = new URLSearchParams(params).toString();
    return this.request(`/extensions/mine${q ? `?${q}` : ''}`);
  }

  async checkExtension(itemType, itemId) {
    return this.request(`/extensions/check/${itemType}/${itemId}`);
  }

  async listEligibleExtensionItems() {
    return this.request('/extensions/eligible');
  }

  async requestExtension({ itemType, itemId, justification }) {
    return this.request('/extensions/request', {
      method: 'POST',
      body: JSON.stringify({ itemType, itemId, justification }),
    });
  }

  async listExtensions(params = {}) {
    const q = new URLSearchParams(
      Object.entries(params).filter(([, v]) => v !== undefined && v !== null && v !== '')
    ).toString();
    return this.request(`/extensions${q ? `?${q}` : ''}`);
  }

  async getPendingExtensionCount() {
    return this.request('/extensions/pending/count');
  }

  async getExtensionHistory({ studentId, courseId, excludeId }) {
    const q = new URLSearchParams(
      Object.entries({ studentId, courseId, excludeId }).filter(([, v]) => v !== undefined && v !== null && v !== '')
    ).toString();
    return this.request(`/extensions/history${q ? `?${q}` : ''}`);
  }

  async getExtensionGrantOptions() {
    return this.request('/extensions/grant-options');
  }

  async grantExtension({ studentId, itemType, itemId, durationHours, note }) {
    return this.request('/extensions/grant', {
      method: 'POST',
      body: JSON.stringify({ studentId, itemType, itemId, durationHours, note }),
    });
  }

  async approveExtension(id, { durationHours, note }) {
    return this.request(`/extensions/${id}/approve`, {
      method: 'POST',
      body: JSON.stringify({ durationHours, note }),
    });
  }

  async denyExtension(id, { note }) {
    return this.request(`/extensions/${id}/deny`, {
      method: 'POST',
      body: JSON.stringify({ note }),
    });
  }

  // Notification endpoints
  async getNotifications() {
    return this.request('/notifications');
  }

  async markNotificationAsRead(notificationId) {
    return this.request(`/notifications/${notificationId}/read`, {
      method: 'PUT'
    });
  }

  async markAllNotificationsAsRead() {
    return this.request('/notifications/read-all', {
      method: 'PUT'
    });
  }

  // Search endpoints
  async search(query, type = 'all') {
    return this.request(`/search?q=${encodeURIComponent(query)}&type=${type}`);
  }

  // Dashboard endpoints
  async getDashboardStats() {
    return this.request('/dashboard/stats');
  }

  // Active Courses / Pending Tasks / Completed Tasks, counted server-side.
  // One small response in place of counting three full payloads on the client.
  async getTaskCounters() {
    return this.request('/dashboard/task-counters');
  }

  async getInstructorStats() {
    return this.request('/dashboard/instructor-stats');
  }

  async getRecentActivity() {
    return this.request('/dashboard/activity');
  }

  async getUpcomingDeadlines() {
    return this.request('/dashboard/deadlines');
  }

};

export default withEngagementApi;
